import { ChangeDetectionStrategy, Component, DOCUMENT, ElementRef, ViewEncapsulation, afterNextRender, computed, inject, signal, viewChild } from '@angular/core';
import { TextFieldModule } from '@angular/cdk/text-field';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { PAGE_COMPONENTS } from '@workbuddy/ui/blocks/page';
import { ButtonComponent } from '@workbuddy/ui/components/button';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { itemCommentBulkDialogBridgeStyles, itemCommentBulkDialogStyleId } from './item.comment.bulk.dialog.theme';

export interface ItemCommentBulkDialogData {
  title: string;
  /** Plural chrome for the note under the box, so one item does not read as "each of the 1 items". */
  count: number;
}

/**
 * Bulk "Add comment" composer for SOR items. A dialog rather than the Actions palette's inline text row:
 * that row rendered without its command label and gave no way to commit the text, and a comment is prose
 * that wants more than one line anyway.
 *
 * The same text is posted to every selected item — each into that item's own thread, which the server
 * resolves per row.
 */
@Component({
  selector: 'item-comment-bulk-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [TextFieldModule, PAGE_COMPONENTS, ButtonComponent],
  styleUrls: ['./item.comment.bulk.dialog.component.less'],
  template: `
    <w-page [title]="data.title" mode="modal" (close)="onClose()">
      <ng-container *w-page-body-content>
        <div class="composer">
          <textarea
            #composer
            class="composer-input"
            cdkTextareaAutosize
            cdkAutosizeMinRows="3"
            cdkAutosizeMaxRows="10"
            placeholder="Enter comments"
            [value]="$draft()"
            (input)="onInput($event)"
            (keydown.meta.enter)="onApply()"
            (keydown.control.enter)="onApply()"></textarea>

          <p class="composer-note">Posted to {{ data.count === 1 ? 'the selected item' : 'each of the ' + data.count + ' selected items' }}.</p>
        </div>
      </ng-container>

      <ng-container *w-page-footer-right-actions>
        <w-button displayStyle="Primary" label="Apply" size="Medium" [disabled]="!$body()" (click)="onApply()" />
      </ng-container>
    </w-page>
  `,
})
export class ItemCommentBulkDialogComponent {

  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly document = inject(DOCUMENT);

  protected readonly data = inject(DynamicDialogConfig).data as ItemCommentBulkDialogData;

  protected readonly $draft = signal('');
  protected readonly $body = computed(() => this.$draft().trim());

  private readonly $composer = viewChild<ElementRef<HTMLTextAreaElement>>('composer');

  public constructor() {

    DesignSystem.Mount(this.document, itemCommentBulkDialogStyleId, itemCommentBulkDialogBridgeStyles);

    // Typing is the only thing to do here, so the dialog opens with the caret already in the box.
    afterNextRender(() => this.$composer()?.nativeElement.focus());
  }

  protected onInput(event: Event): void {

    this.$draft.set((event.target as HTMLTextAreaElement).value);
  }

  protected onApply(): void {

    const body = this.$body();
    if (!body) return;

    this.dialogRef.close(body);
  }

  protected onClose(): void {

    this.dialogRef.close();
  }
}
